import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import JsBarcode from 'jsbarcode'
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'

export function reportOrderNoWMS(dados_pedido, tipoImpressao) {
  try {
    const {
      cobra_frete,
      itens: clientes,
      valor_frete: frete,
      detalhes_pedido: obs,
      cliente: NomeCliente,
      numeroorc,
      orderid: idpedidoloja,
      data,
      cpf,
      dadosDoCliente: DadosCliente,
      EnderecoEnt,
      tipo_pedido: tipoVenda,
      formaPagamento,
      empresa,
      duplicatas,
      valorDevolvido: valorDevolvido,
    } = dados_pedido

    const empresaInfo = empresa?.[0] || {}
    const dataFormatada = format(new Date(data), 'dd/MM/yyyy', {
      locale: ptBR,
    })

    // Utilitários
    const formatarCPFouCNPJ = (cpfOuCnpj) => {
      if (!cpfOuCnpj) return 'N/I'
      const only = String(cpfOuCnpj).replace(/\D/g, '')
      if (only.length === 11)
        return only.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4')
      if (only.length === 14)
        return only.replace(
          /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
          '$1.$2.$3/$4-$5',
        )
      return cpfOuCnpj
    }

    const generateBarcode = (data) => {
      const canvas = document.createElement('canvas')
      JsBarcode(canvas, String(data || ''), {
        format: 'CODE128',
        height: 30,
        displayValue: false,
        margin: 0,
      })
      return canvas.toDataURL('image/png')
    }

    const formatCurrency = (value) => {
      const n = Number(value || 0)
      return n.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
      })
    }

    const formatNumber = (value) => {
      const n = Number(value || 0)
      return n.toFixed(2).replace('.', ',')
    }

    // Configuração do PDF
    pdfMake.addVirtualFileSystem(pdfFonts)

    // Paleta
    const primaryColor = '#1e40af'
    const accentColor = '#3b82f6'
    const successColor = '#10b981'
    const dangerColor = '#dc2626'
    const darkColor = '#1f2937'
    const lightGray = '#f8fafc'
    const borderColor = '#e5e7eb'

    // Estilos
    const styles = {
      title: {
        fontSize: 20,
        bold: true,
        color: primaryColor,
        margin: [0, 0, 0, 8],
      },
      subtitle: {
        fontSize: 12,
        bold: true,
        color: accentColor,
        margin: [0, 12, 0, 6],
      },
      sectionTitle: {
        fontSize: 11,
        bold: true,
        color: primaryColor,
        margin: [0, 8, 0, 4],
      },
      tableHeader: {
        fillColor: primaryColor,
        color: 'white',
        bold: true,
        fontSize: 8,
        alignment: 'center',
      },
      tableCell: { fontSize: 7, margin: [2, 3, 2, 3] },
      tableCellBold: { fontSize: 7, bold: true, margin: [2, 3, 2, 3] },
      totalRow: { fillColor: lightGray, fontSize: 8, bold: true },
      highlight: { fillColor: '#eff6ff', color: primaryColor },
    }

    // Totais
    const cpfFormatado = formatarCPFouCNPJ(cpf)
    const barcodeImage = generateBarcode(idpedidoloja)

    const totalItens = (clientes || []).reduce(
      (acc, item) => acc + Number(item.valorunitario) * Number(item.quantidade),
      0,
    )
    const totalDescontos = (clientes || []).reduce(
      (acc, item) => acc + Number(item.desconto || 0),
      0,
    )
    const valorFrete = cobra_frete === 'S' ? parseFloat(frete) || 0 : 0
    const totalBruto = totalItens - totalDescontos + valorFrete
    const temDevolucao = Number(valorDevolvido) > 0
    const totalLiquido = temDevolucao
      ? totalBruto - Number(valorDevolvido || 0)
      : totalBruto

    // Header empresa
    const companyHeader = {
      margin: [0, 0, 0, 15],
      table: {
        widths: ['*', 100],
        body: [
          [
            {
              text: [
                {
                  text: empresaInfo.EMP_RAZAO_SOCIAL || '',
                  fontSize: 12,
                  bold: true,
                  color: darkColor,
                },
                {
                  text:
                    '\n' +
                    [empresaInfo.EMP_ENDERECO, empresaInfo.EMP_NUMERO]
                      .filter(Boolean)
                      .join(', '),
                  fontSize: 8,
                  color: '#6b7280',
                },
                {
                  text:
                    '\n' +
                    [empresaInfo.EMP_CIDADE, empresaInfo.EMP_ESTADO]
                      .filter(Boolean)
                      .join(' - ') +
                    (empresaInfo.EMP_CEP
                      ? ` | CEP: ${empresaInfo.EMP_CEP}`
                      : ''),
                  fontSize: 8,
                  color: '#6b7280',
                },
                {
                  text:
                    '\n' +
                    (empresaInfo.EMP_CNPJ
                      ? `CNPJ: ${empresaInfo.EMP_CNPJ}`
                      : '') +
                    (empresaInfo.EMP_TELEFONE
                      ? ` | Tel: ${empresaInfo.EMP_TELEFONE}`
                      : ''),
                  fontSize: 8,
                  color: '#6b7280',
                },
              ],
              border: [false, false, false, false],
            },
            {
              stack: [
                {
                  text: `Nº ${numeroorc || 'N/I'}`,
                  fontSize: 10,
                  bold: true,
                  color: primaryColor,
                  alignment: 'center',
                  margin: [0, 0, 0, 5],
                },
                {
                  image: barcodeImage,
                  width: 90,
                  alignment: 'center',
                },
              ],
              border: [false, false, false, false],
            },
          ],
        ],
      },
      layout: 'noBorders',
    }

    // Info pedido
    const orderInfo = {
      margin: [0, 0, 0, 10],
      table: {
        widths: ['*', '*', '*'],
        body: [
          [
            {
              text: [
                {
                  text:
                    'PEDIDO DE ' + String(tipoVenda || 'venda').toUpperCase(),
                  fontSize: 11,
                  bold: true,
                  color: primaryColor,
                },
                {
                  text: '\nNº ' + (numeroorc ?? ''),
                  fontSize: 9,
                  bold: true,
                  color: accentColor,
                },
              ],
              fillColor: lightGray,
              margin: [8, 8, 8, 8],
              border: [false, false, true, false],
              borderColor: [null, null, borderColor, null],
            },
            {
              text: [
                { text: 'DATA\n', fontSize: 7, color: '#6b7280' },
                {
                  text: dataFormatada,
                  fontSize: 10,
                  bold: true,
                  color: darkColor,
                },
                { text: '\n\nORDER ID\n', fontSize: 7, color: '#6b7280' },
                { text: idpedidoloja || '', fontSize: 9, color: darkColor },
              ],
              fillColor: lightGray,
              margin: [8, 8, 8, 8],
              border: [false, false, true, false],
              borderColor: [null, null, borderColor, null],
            },
            {
              text: [
                { text: 'FORMA DE PAGAMENTO\n', fontSize: 7, color: '#6b7280' },
                {
                  text: formaPagamento || 'N/I',
                  fontSize: 9,
                  bold: true,
                  color: darkColor,
                },
              ],
              fillColor: lightGray,
              margin: [8, 8, 8, 8],
              border: [false, false, false, false],
            },
          ],
        ],
      },
      layout: {
        hLineWidth: () => 0,
        vLineWidth: (i) => (i === 1 || i === 2 ? 1 : 0),
        vLineColor: () => borderColor,
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0,
      },
    }

    // Dados cliente
    const clientInfo = {
      margin: [0, 0, 0, 15],
      table: {
        widths: ['*', '*'],
        body: [
          [
            {
              text: [
                {
                  text: 'CLIENTE\n',
                  fontSize: 9,
                  bold: true,
                  color: primaryColor,
                },
                {
                  text: (NomeCliente || '') + '\n',
                  fontSize: 10,
                  bold: true,
                  color: darkColor,
                },
                {
                  text: 'CPF/CNPJ: ' + cpfFormatado + '\n',
                  fontSize: 8,
                  color: '#6b7280',
                },
                {
                  text: 'Tel: ' + (DadosCliente?.pes_celular || 'N/I'),
                  fontSize: 8,
                  color: '#6b7280',
                },
              ],
              fillColor: lightGray,
              margin: [10, 10, 10, 10],
              border: [false, false, true, false],
              borderColor: [null, null, borderColor, null],
            },
            {
              text: [
                {
                  text: 'ENDEREÇO\n',
                  fontSize: 9,
                  bold: true,
                  color: primaryColor,
                },
                {
                  text:
                    [DadosCliente?.pes_logradouro, DadosCliente?.pes_numero]
                      .filter(Boolean)
                      .join(', ') + '\n',
                  fontSize: 8,
                  color: darkColor,
                },
                {
                  text: (DadosCliente?.pes_complemento || '') + '\n',
                  fontSize: 8,
                  color: darkColor,
                },
                {
                  text:
                    [
                      DadosCliente?.pes_bairro,
                      [DadosCliente?.pes_cidade, DadosCliente?.pes_uf]
                        .filter(Boolean)
                        .join(' - '),
                    ]
                      .filter(Boolean)
                      .join(', ') + '\n',
                  fontSize: 8,
                  color: darkColor,
                },
                {
                  text: 'CEP: ' + (DadosCliente?.pes_cep || ''),
                  fontSize: 8,
                  color: darkColor,
                },
              ],
              fillColor: lightGray,
              margin: [10, 10, 10, 10],
              border: [false, false, false, false],
            },
          ],
        ],
      },
      layout: {
        hLineWidth: () => 0,
        vLineWidth: (i) => (i === 1 ? 1 : 0),
        vLineColor: () => borderColor,
        paddingLeft: () => 0,
        paddingRight: () => 0,
        paddingTop: () => 0,
        paddingBottom: () => 0,
      },
    }

    // Endereço entrega
    const deliveryInfo = EnderecoEnt
      ? {
          margin: [0, 0, 0, 15],
          table: {
            widths: ['*'],
            body: [
              [
                {
                  text: [
                    {
                      text: '🚚 ENDEREÇO DE ENTREGA\n',
                      fontSize: 9,
                      bold: true,
                      color: primaryColor,
                    },
                    { text: EnderecoEnt, fontSize: 8, color: darkColor },
                  ],
                  fillColor: '#fef3c7',
                  margin: [10, 8, 10, 8],
                },
              ],
            ],
          },
          layout: 'noBorders',
        }
      : null

    // Itens - Versões para diferentes tipos de impressão
    const buildItemsTable = () => {
      if (tipoImpressao === 'Resumo') {
        // Versão resumida - 6 colunas
        const tableBody = [
          [
            { text: 'CÓDIGO', style: 'tableHeader' },
            { text: 'DESCRIÇÃO', style: 'tableHeader' },
            { text: 'QTD', style: 'tableHeader' },
            { text: 'VALOR UNIT.', style: 'tableHeader' },
            { text: 'TOTAL', style: 'tableHeader' },
            { text: 'OBS', style: 'tableHeader' },
          ],
        ]

        let currentGroup = null
        let groupTotal = 0

        ;(clientes || []).forEach((item, index) => {
          const itemTotal = Number(item.valorunitario) * Number(item.quantidade)

          if (item.grupo_item && item.grupo_item !== currentGroup) {
            if (currentGroup !== null) {
              tableBody.push([
                {
                  text: `SUBTOTAL ${currentGroup}: ${formatCurrency(
                    groupTotal,
                  )}`,
                  colSpan: 6,
                  style: 'totalRow',
                  alignment: 'right',
                  margin: [0, 5, 0, 5],
                },
                {},
                {},
                {},
                {},
                {}, // Células vazias para completar o colSpan
              ])
            }
            currentGroup = item.grupo_item
            groupTotal = 0
            tableBody.push([
              {
                text: `📦 AMBIENTE: ${currentGroup}`,
                colSpan: 6,
                fillColor: '#e0f2fe',
                color: primaryColor,
                bold: true,
                fontSize: 8,
                margin: [0, 4, 0, 4],
              },
              {},
              {},
              {},
              {},
              {}, // Células vazias para completar o colSpan
            ])
          }

          groupTotal += itemTotal

          // Descrição resumida - apenas primeira palavra
          const descricaoResumida = (item.descricao || '')
            .split(' ')[0]
            .toString()
            .toUpperCase()

          tableBody.push([
            {
              text: (item.codigo || 'N/I').toString().toUpperCase(),
              style: 'tableCell',
              color: accentColor,
              bold: true,
            },
            {
              text: descricaoResumida,
              style: 'tableCell',
            },
            {
              text: formatNumber(item.quantidade),
              style: 'tableCell',
              alignment: 'center',
              fillColor: index % 2 === 0 ? '#f9fafb' : 'white',
            },
            {
              text: formatCurrency(item.valorunitario),
              style: 'tableCell',
              alignment: 'right',
            },
            {
              text: formatCurrency(itemTotal),
              style: 'tableCellBold',
              alignment: 'right',
              color: successColor,
            },
            {
              text: item.obs || '-',
              style: 'tableCell',
              fontSize: 6,
              color: '#6b7280',
            },
          ])
        })

        if (currentGroup !== null) {
          tableBody.push([
            {
              text: `SUBTOTAL ${currentGroup}: ${formatCurrency(groupTotal)}`,
              colSpan: 6,
              style: 'totalRow',
              alignment: 'right',
              margin: [0, 5, 0, 5],
            },
            {},
            {},
            {},
            {},
            {}, // Células vazias para completar o colSpan
          ])
        }

        return {
          table: {
            headerRows: 1,
            widths: [50, '*', 35, 55, 55, 60], // 6 colunas
            body: tableBody,
          },
          layout: {
            fillColor: (rowIndex) => (rowIndex === 0 ? primaryColor : null),
            hLineWidth: (i, node) =>
              i === 0 || i === node.table.body.length ? 1 : 0.3,
            vLineWidth: () => 0.3,
            hLineColor: () => borderColor,
            vLineColor: () => borderColor,
          },
        }
      } else {
        // Versão completa - 6 colunas (removidas as colunas de localização comentadas)
        const tableBody = [
          [
            { text: 'CÓDIGO', style: 'tableHeader' },
            { text: 'DESCRIÇÃO', style: 'tableHeader' },
            { text: 'QTD', style: 'tableHeader' },
            { text: 'VL.UNIT', style: 'tableHeader' },
            { text: 'TOTAL', style: 'tableHeader' },
            { text: 'OBS', style: 'tableHeader' },
          ],
        ]

        let currentGroup = null
        let groupTotal = 0

        ;(clientes || []).forEach((item, index) => {
          const itemTotal = Number(item.valorunitario) * Number(item.quantidade)

          if (item.grupo_item && item.grupo_item !== currentGroup) {
            if (currentGroup !== null) {
              tableBody.push([
                {
                  text: `SUBTOTAL ${currentGroup}: ${formatCurrency(
                    groupTotal,
                  )}`,
                  colSpan: 6,
                  style: 'totalRow',
                  alignment: 'right',
                  margin: [0, 4, 0, 4],
                },
                {},
                {},
                {},
                {},
                {}, // Células vazias para completar o colSpan
              ])
            }
            currentGroup = item.grupo_item
            groupTotal = 0
            tableBody.push([
              {
                text: `📦 AMBIENTE: ${currentGroup}`,
                colSpan: 6,
                fillColor: '#e0f2fe',
                color: primaryColor,
                bold: true,
                fontSize: 7,
                margin: [0, 3, 0, 3],
              },
              {},
              {},
              {},
              {},
              {}, // Células vazias para completar o colSpan
            ])
          }

          groupTotal += itemTotal

          tableBody.push([
            {
              text: (item.codigo || 'N/I').toString().toUpperCase(),
              style: 'tableCell',
              color: accentColor,
              bold: true,
              fontSize: 6,
            },
            {
              text: (item.descricao || 'N/I').toString().toUpperCase(),
              style: 'tableCell',
              fontSize: 6,
            },
            {
              text: formatNumber(item.quantidade),
              style: 'tableCell',
              alignment: 'center',
              fontSize: 6,
              fillColor: index % 2 === 0 ? '#f9fafb' : 'white',
            },
            {
              text: formatCurrency(item.valorunitario),
              style: 'tableCell',
              alignment: 'right',
              fontSize: 6,
            },
            {
              text: formatCurrency(itemTotal),
              style: 'tableCellBold',
              alignment: 'right',
              color: successColor,
              fontSize: 6,
            },
            {
              text: item.obs || '-',
              style: 'tableCell',
              fontSize: 5,
              color: '#6b7280',
            },
          ])
        })

        if (currentGroup !== null) {
          tableBody.push([
            {
              text: `SUBTOTAL ${currentGroup}: ${formatCurrency(groupTotal)}`,
              colSpan: 6,
              style: 'totalRow',
              alignment: 'right',
              margin: [0, 4, 0, 4],
            },
            {},
            {},
            {},
            {},
            {}, // Células vazias para completar o colSpan
          ])
        }

        return {
          table: {
            headerRows: 1,
            widths: [40, '*', 30, 45, 45, 40], // 6 colunas
            body: tableBody,
          },
          layout: {
            fillColor: (rowIndex) => (rowIndex === 0 ? primaryColor : null),
            hLineWidth: (i, node) =>
              i === 0 || i === node.table.body.length ? 1 : 0.3,
            vLineWidth: () => 0.3,
            hLineColor: () => borderColor,
            vLineColor: () => borderColor,
          },
        }
      }
    }

    // Resumo financeiro otimizado
    const buildFinancialCards = () => {
      const cards = [
        {
          text: [
            { text: 'SUBTOTAL\n', fontSize: 8, color: '#6b7280' },
            {
              text: formatCurrency(totalItens),
              fontSize: 10,
              bold: true,
              color: darkColor,
            },
          ],
          fillColor: lightGray,
          margin: [8, 8, 8, 8],
          alignment: 'center',
        },
        {
          text: [
            { text: 'FRETE\n', fontSize: 8, color: '#6b7280' },
            {
              text: formatCurrency(valorFrete),
              fontSize: 10,
              bold: true,
              color: darkColor,
            },
          ],
          fillColor: lightGray,
          margin: [8, 8, 8, 8],
          alignment: 'center',
        },
        {
          text: [
            { text: 'DESCONTO\n', fontSize: 8, color: '#6b7280' },
            {
              text: formatCurrency(totalDescontos),
              fontSize: 10,
              bold: true,
              color: dangerColor,
            },
          ],
          fillColor: lightGray,
          margin: [8, 8, 8, 8],
          alignment: 'center',
        },
        {
          text: [
            { text: 'TOTAL GERAL\n', fontSize: 8, color: '#6b7280' },
            {
              text: formatCurrency(totalBruto),
              fontSize: 11,
              bold: true,
              color: successColor,
            },
          ],
          fillColor: '#f0fdf4',
          margin: [8, 8, 8, 8],
          alignment: 'center',
        },
      ]

      if (temDevolucao) {
        cards.push({
          text: [
            { text: 'VALOR ABATIDO\n', fontSize: 8, color: '#6b7280' },
            {
              text: formatCurrency(valorDevolvido),
              fontSize: 10,
              bold: true,
              color: dangerColor,
            },
          ],
          fillColor: '#fee2e2',
          margin: [8, 8, 8, 8],
          alignment: 'center',
        })

        cards.push({
          text: [
            { text: 'LÍQUIDO\n', fontSize: 8, color: '#6b7280' },
            {
              text: formatCurrency(totalLiquido),
              fontSize: 11,
              bold: true,
              color: successColor,
            },
          ],
          fillColor: '#ecfeff',
          margin: [8, 8, 8, 8],
          alignment: 'center',
        })
      }

      return {
        margin: [0, 15, 0, 0],
        table: {
          widths: Array(cards.length).fill('*'),
          body: [cards],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: (i) => (i > 0 ? 1 : 0),
          vLineColor: () => borderColor,
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
      }
    }

    // Parcelas
    const installmentsTable =
      duplicatas?.length > 0
        ? {
            margin: [0, 15, 0, 0],
            text: [{ text: '💳 PARCELAS', style: 'sectionTitle' }],
          }
        : null

    const installmentsData =
      duplicatas?.length > 0
        ? {
            margin: [0, 8, 0, 0],
            table: {
              headerRows: 1,
              widths: ['*', '*', '*', '*'],
              body: [
                [
                  { text: 'PARCELA', style: 'tableHeader' },
                  { text: 'VENCIMENTO', style: 'tableHeader' },
                  { text: 'VALOR', style: 'tableHeader' },
                  { text: 'OBSERVAÇÕES', style: 'tableHeader' },
                ],
                ...duplicatas.map((dup, i) => [
                  {
                    text: dup?.numero || `${i + 1}ª`,
                    style: 'tableCell',
                    alignment: 'center',
                  },
                  {
                    text: dup?.datavencimento || 'N/I',
                    style: 'tableCell',
                    alignment: 'center',
                  },
                  {
                    text: formatCurrency(dup?.valor || 0),
                    style: 'tableCellBold',
                    alignment: 'right',
                    color: successColor,
                  },
                  { text: dup?.obs || '-', style: 'tableCell', fontSize: 6 },
                ]),
              ],
            },
            layout: {
              fillColor: (rowIndex) =>
                rowIndex === 0
                  ? primaryColor
                  : rowIndex % 2 === 0
                    ? '#f9fafb'
                    : 'white',
              hLineWidth: () => 0.3,
              vLineWidth: () => 0.3,
              hLineColor: () => borderColor,
              vLineColor: () => borderColor,
            },
          }
        : null

    // Observações
    const observations = {
      margin: [0, 15, 0, 0],
      table: {
        widths: ['*'],
        body: [
          [
            {
              text: [
                {
                  text: '📝 OBSERVAÇÕES\n',
                  fontSize: 9,
                  bold: true,
                  color: primaryColor,
                },
                {
                  text: obs || 'Nenhuma observação adicional.',
                  fontSize: 8,
                  color: darkColor,
                },
              ],
              fillColor: '#f8fafc',
              margin: [10, 8, 10, 8],
            },
          ],
        ],
      },
      layout: 'noBorders',
    }

    // Footer
    const footer = (currentPage, pageCount) => ({
      margin: [20, 8, 20, 0],
      table: {
        widths: ['*', '*'],
        body: [
          [
            {
              text: [
                { text: '🖨️ Impresso em ', fontSize: 7, color: '#9ca3af' },
                {
                  text: format(new Date(), 'dd/MM/yyyy às HH:mm', {
                    locale: ptBR,
                  }),
                  fontSize: 7,
                  color: '#4b5563',
                },
                {
                  text: '\nZipHub Gestão - Sistema Integrado',
                  fontSize: 6,
                  color: '#9ca3af',
                },
              ],
              border: [false, false, false, false],
            },
            {
              text: [
                {
                  text: `Página ${currentPage} de ${pageCount}`,
                  fontSize: 7,
                  color: '#4b5563',
                },
              ],
              alignment: 'right',
              border: [false, false, false, false],
            },
          ],
        ],
      },
      layout: 'noBorders',
    })

    // Documento
    const docDefinition = {
      pageSize: 'A4',
      pageMargins: [25, 45, 25, 45],
      content: [
        companyHeader,
        orderInfo,
        clientInfo,
        ...(deliveryInfo ? [deliveryInfo] : []),
        buildItemsTable(),
        buildFinancialCards(),
        ...(installmentsTable ? [installmentsTable] : []),
        ...(installmentsData ? [installmentsData] : []),
        observations,
      ],
      footer: footer,
      styles: styles,
      defaultStyle: { font: 'Roboto', fontSize: 9, lineHeight: 1.2 },
    }

    // Gerar PDF
    pdfMake.createPdf(docDefinition).open()
  } catch (error) {
    console.error('Erro ao gerar PDF:', error)
    alert('Erro ao gerar o relatório. Verifique os dados e tente novamente.')
  }
}
